import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const US_STOCK_LIST = [
  "AAPL", "MSFT", "GOOGL", "AMZN", "NVDA", "META", "TSLA",
  "SPY",  "QQQ",  "IWM",  "DIA",  "GLD",  "TLT",  "XLF",
  "AMD",  "NFLX", "COIN", "PLTR", "SNOW", "BABA",  "VXX",
];

const SPEED_OPTIONS = [
  { label: "1× (real-time)",  value: 1 },
  { label: "10× (6 min/sec)", value: 10 },
  { label: "60× (1 bar/sec)", value: 60 },
  { label: "300× (5 bars/sec)", value: 300 },
];

export default function MarketReplayPage() {
  const [status,   setStatus]   = useState(null);
  const [pollErr,  setPollErr]  = useState(false);
  const [msg,      setMsg]      = useState(null);

  // form state
  const [symbols,    setSymbols]    = useState(["AAPL", "SPY"]);
  const [startDate,  setStartDate]  = useState("2024-01-02");
  const [speed,      setSpeed]      = useState(60);
  const [busy,       setBusy]       = useState(false);

  const flash = (text, ok = true) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 4000);
  };

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get("/replay/status");
      setStatus(res.data);
      setPollErr(false);
    } catch {
      setPollErr(true);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, 2000);
    return () => clearInterval(id);
  }, [fetchStatus]);

  const toggleSymbol = (sym) => {
    setSymbols(prev =>
      prev.includes(sym) ? prev.filter(s => s !== sym) : [...prev, sym]
    );
  };

  const handleLoadAndStart = async () => {
    if (symbols.length === 0) { flash("Select at least one symbol.", false); return; }
    if (!startDate)           { flash("Start date is required.",     false); return; }
    setBusy(true);
    try {
      const loadRes = await axios.post("/replay/load", { symbols, start_date: startDate });
      const loaded  = loadRes.data.loaded_symbols || [];
      const skipped = loadRes.data.skipped_symbols || [];

      if (loaded.length === 0) {
        flash(
          `No history files found for selected symbols. ` +
          `Run: python scripts/fetch_tradier_history.py --symbols ${symbols.join(",")} ` +
          `--start ${startDate} --end <end-date> inside the backend container.`,
          false
        );
        setBusy(false);
        return;
      }

      await axios.post("/replay/start", { speed });
      const skipMsg = skipped.length ? ` (${skipped.join(", ")} skipped — no history files)` : "";
      flash(`Replay started for ${loaded.join(", ")}${skipMsg}.`);
      fetchStatus();
    } catch (e) {
      flash(e?.response?.data?.error || "Failed to start replay.", false);
    } finally {
      setBusy(false);
    }
  };

  const handleStop = async () => {
    try {
      await axios.post("/replay/stop");
      flash("Replay stopped.");
      fetchStatus();
    } catch (e) {
      flash(e?.response?.data?.error || "Failed to stop replay.", false);
    }
  };

  const isRunning = status?.running === true;

  return (
    <div style={{ padding: 25, background: "#f4f6f9", minHeight: "100vh" }}>
      <h2 style={pageTitle}>Market Replay</h2>
      <p style={{ color: "#666", fontSize: 13, marginTop: -12, marginBottom: 20 }}>
        Play back historical 1-min OHLCV data from Tradier sandbox as a simulated live feed.
        History files must be downloaded first using the CLI script.
      </p>

      {msg && (
        <div style={{ ...toast, background: msg.ok ? "#28a745" : "#dc3545" }}>{msg.text}</div>
      )}

      {/* ── STATUS PANEL ── */}
      <div style={card}>
        <div style={cardHeader}>
          <h5 style={cardTitle}>Replay Status</h5>
          {pollErr && <span style={{ fontSize: 12, color: "#dc3545", marginLeft: 10 }}>
            PLAYBACK_MODE may be off — /replay endpoints not registered
          </span>}
        </div>
        <div style={cardBody}>
          {!status ? (
            <p style={{ color: "#888", fontSize: 13 }}>Connecting…</p>
          ) : (
            <>
              <div style={statusRow}>
                <StatusPill running={isRunning} />
                {status.current_ts && (
                  <span style={metaChip}>
                    {status.current_ts.replace("T", " ").slice(0, 19)}
                  </span>
                )}
                {isRunning && (
                  <span style={metaChip}>{status.speed}× speed</span>
                )}
                {status.progress_pct > 0 && (
                  <span style={metaChip}>{status.progress_pct}% complete</span>
                )}
              </div>

              {/* Progress bar */}
              {status.total_bars > 0 && (
                <div style={progressTrack}>
                  <div style={{ ...progressBar, width: `${status.progress_pct}%` }} />
                </div>
              )}

              {/* Live prices table */}
              {status.prices && Object.keys(status.prices).length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#6c757d",
                    textTransform: "uppercase", marginBottom: 8 }}>
                    Live Replay Prices
                  </div>
                  <div style={priceGrid}>
                    {Object.entries(status.prices).sort().map(([sym, price]) => (
                      <div key={sym} style={priceCard}>
                        <div style={priceSymbol}>{sym}</div>
                        <div style={priceValue}>${Number(price).toFixed(2)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {isRunning && (
                <div style={{ marginTop: 16 }}>
                  <button onClick={handleStop} style={btnDanger}>Stop Replay</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── START FORM ── */}
      {!isRunning && (
        <div style={card}>
          <div style={cardHeader}><h5 style={cardTitle}>Start New Replay</h5></div>
          <div style={cardBody}>

            {/* Symbols */}
            <div style={{ marginBottom: 20 }}>
              <div style={fieldLabel}>Symbols</div>
              <div style={symbolGrid}>
                {US_STOCK_LIST.map(sym => (
                  <label key={sym} style={{
                    ...symbolChip,
                    background: symbols.includes(sym) ? "#e8f4fd" : "#f8f9fa",
                    border: `1px solid ${symbols.includes(sym) ? "#007bff" : "#dee2e6"}`,
                    color: symbols.includes(sym) ? "#007bff" : "#495057",
                  }}>
                    <input
                      type="checkbox"
                      checked={symbols.includes(sym)}
                      onChange={() => toggleSymbol(sym)}
                      style={{ marginRight: 5 }}
                    />
                    {sym}
                  </label>
                ))}
              </div>
              <div style={{ marginTop: 6, fontSize: 12, color: "#888" }}>
                {symbols.length} selected —{" "}
                <button onClick={() => setSymbols([...US_STOCK_LIST])} style={linkBtn}>All</button>
                {" / "}
                <button onClick={() => setSymbols([])} style={linkBtn}>None</button>
              </div>
            </div>

            {/* Date + Speed */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
              <div>
                <div style={fieldLabel}>Start Date</div>
                <input
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  style={input}
                />
                <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
                  History files must cover this date.
                </div>
              </div>
              <div>
                <div style={fieldLabel}>Playback Speed</div>
                <select value={speed} onChange={e => setSpeed(Number(e.target.value))} style={input}>
                  {SPEED_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* CLI hint */}
            <div style={hintBox}>
              <strong>Need history files?</strong> Run inside the backend container:
              <pre style={hintCode}>
{`docker compose exec backend python scripts/fetch_tradier_history.py \\
  --symbols ${symbols.slice(0, 3).join(",")}${symbols.length > 3 ? ",…" : ""} \\
  --start ${startDate} --end 2024-01-31`}
              </pre>
            </div>

            <button
              onClick={handleLoadAndStart}
              disabled={busy || symbols.length === 0}
              style={{ ...btnPrimary, marginTop: 16, opacity: (busy || symbols.length === 0) ? 0.6 : 1 }}>
              {busy ? "Loading…" : "Load & Start Replay"}
            </button>
          </div>
        </div>
      )}

      {/* ── CLI REFERENCE ── */}
      <div style={card}>
        <div style={cardHeader}><h5 style={cardTitle}>How It Works</h5></div>
        <div style={{ padding: "16px 20px", fontSize: 13, color: "#444", lineHeight: 1.7 }}>
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            <li>Download history files once using the CLI script (see hint above).</li>
            <li>Select symbols and a start date on this page, then click <strong>Load &amp; Start Replay</strong>.</li>
            <li>The backend streams prices via WebSocket on port 5002 and the cycle runner uses replay prices instead of live Tradier data.</li>
            <li>Use <strong>Stop Replay</strong> to pause; the engine reverts to live data when <code>PLAYBACK_MODE=false</code>.</li>
          </ol>
        </div>
      </div>
    </div>
  );
}

/* ── Sub-components ──────────────────────────────────────────────── */

function StatusPill({ running }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "4px 12px", borderRadius: 20, fontWeight: 700, fontSize: 13,
      background: running ? "#d4edda" : "#f8d7da",
      color: running ? "#155724" : "#721c24",
    }}>
      <span style={{
        width: 8, height: 8, borderRadius: "50%",
        background: running ? "#28a745" : "#dc3545",
        display: "inline-block",
        animation: running ? "pulse 1.2s infinite" : "none",
      }} />
      {running ? "Running" : "Stopped"}
    </span>
  );
}

/* ── Styles ──────────────────────────────────────────────────────── */

const pageTitle = { fontSize: 22, fontWeight: 700, marginBottom: 6, color: "#2c3e50" };
const toast = {
  position: "fixed", top: 20, right: 20, zIndex: 9999, maxWidth: 500,
  padding: "10px 18px", borderRadius: 8, color: "#fff", fontWeight: 600, fontSize: 13,
};
const card = {
  background: "#fff", borderRadius: 12, marginBottom: 24,
  boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
};
const cardHeader = {
  padding: "14px 20px", borderBottom: "1px solid #f0f0f0",
  display: "flex", alignItems: "center",
};
const cardTitle = { margin: 0, fontSize: 15, fontWeight: 600, color: "#2c3e50" };
const cardBody = { padding: 20 };
const fieldLabel = {
  fontSize: 12, fontWeight: 600, color: "#6c757d",
  textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6,
};
const input = { padding: "7px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 13, width: "100%" };
const statusRow = { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12 };
const metaChip = {
  padding: "3px 10px", borderRadius: 12, background: "#e9ecef",
  fontSize: 12, color: "#495057", fontWeight: 600,
};
const progressTrack = {
  height: 6, background: "#e9ecef", borderRadius: 3, overflow: "hidden", marginBottom: 12,
};
const progressBar = { height: "100%", background: "#007bff", transition: "width 0.5s" };
const priceGrid = {
  display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))", gap: 8,
};
const priceCard = {
  background: "#f8f9fa", borderRadius: 8, padding: "8px 10px", textAlign: "center",
  border: "1px solid #e9ecef",
};
const priceSymbol = { fontSize: 11, fontWeight: 700, color: "#6c757d", marginBottom: 2 };
const priceValue = { fontSize: 14, fontWeight: 700, color: "#2c3e50" };
const symbolGrid = {
  display: "flex", flexWrap: "wrap", gap: 8,
};
const symbolChip = {
  display: "inline-flex", alignItems: "center", padding: "5px 10px", borderRadius: 20,
  fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.15s",
};
const linkBtn = {
  background: "none", border: "none", color: "#007bff", cursor: "pointer",
  padding: 0, fontSize: 12, textDecoration: "underline",
};
const hintBox = {
  background: "#f8f9fa", borderRadius: 8, padding: "12px 14px",
  border: "1px solid #e9ecef", fontSize: 12, color: "#555",
};
const hintCode = {
  margin: "6px 0 0", padding: "8px 10px", background: "#2c3e50",
  color: "#a8d8a8", borderRadius: 6, fontSize: 11, overflowX: "auto",
};
const btnPrimary = {
  padding: "9px 22px", borderRadius: 8, border: "none",
  background: "#007bff", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600,
};
const btnDanger = {
  padding: "8px 20px", borderRadius: 8, border: "none",
  background: "#dc3545", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600,
};
